/**
 * MissingMiddleTour
 * ─────────────────
 * 60-second self-running explainer of the home page. A floating
 * "Take the Tour" button (bottom-right) launches a 4-step guided
 * overlay any sales rep, coach, or customer can launch solo to walk
 * the chain narrative end-to-end.
 *
 * Implementation is deliberately library-free (no react-joyride etc.)
 * — just a fixed backdrop, a tooltip card, and a CSS spotlight ring
 * applied to the target element via a `data-tour-active="true"`
 * attribute. Steps are wired by `data-testid` so we don't couple the
 * tour to DOM structure.
 *
 * Step copy is fully editable in `/admin/messaging` under
 * `tour.step_{1,2,3,4}_{title,body}` — your CMO can refine the
 * narrative without a deploy.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { PlayCircle, X as XIcon, ArrowLeft, ArrowRight as ArrowRightIcon, Sparkles, Link2, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { useMessaging } from '../../hooks/useMessaging';
import { useAuth } from '../../context/AuthContext';
import { useChainLeak } from '../../hooks/useChainLeak';
import { useDemoTools } from '../../hooks/useDemoTools';
import api from '../../lib/api';

const SHARABLE_ORG_PARAM = 'org';

// Re-applied each render so any in-progress hot reload picks it up.
const SPOTLIGHT_STYLE_ID = 'mc-tour-spotlight-style';
function ensureSpotlightCss() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SPOTLIGHT_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SPOTLIGHT_STYLE_ID;
  style.textContent = `
    [data-tour-active="true"] {
      position: relative !important;
      z-index: 60 !important;
      box-shadow:
        0 0 0 4px rgba(34, 211, 238, 0.95),
        0 0 0 12px rgba(34, 211, 238, 0.35),
        0 0 60px 8px rgba(8, 145, 178, 0.55) !important;
      border-radius: 14px !important;
      transition: box-shadow 200ms ease;
    }
  `;
  document.head.appendChild(style);
}

export default function MissingMiddleTour() {
  const [open, setOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const prevTargetRef = useRef(null);

  // Sales-tool affordance is super-admin-only — customers don't need
  // to grab the demo link to themselves.
  const { isSuperAdmin, viewAsOrg } = useAuth();
  const [demoTools] = useDemoTools();

  // Editable copy — sourced via the messaging layer so a CMO can
  // refine narrative without a deploy.
  const s1Title = useMessaging('tour.step_1_title', 'This is the Learning Impact Chain');
  const s1Body  = useMessaging('tour.step_1_body',  'Mission Control\u2019s primary instrument. It shows how value flows from learning investment all the way through to financial impact \u2014 in five executive stages.');
  const s2Title = useMessaging('tour.step_2_title', 'The Missing Middle lives here');
  const s2Body  = useMessaging('tour.step_2_body',  'Capability, Confidence, Application, Behavior and Performance. The invisible space between learning investment and business value \u2014 where the most value is created, and the most value is lost.');
  const s3Title = useMessaging('tour.step_3_title', 'Here is where value is leaking');
  const s3Body  = useMessaging('tour.step_3_body',  'The discount labels between stages show how many points of value drop along the chain. The largest drop is your leak \u2014 the single highest-leverage thing to fix.');
  const s4Title = useMessaging('tour.step_4_title', 'And here is what we do about it');
  const s4Body  = useMessaging('tour.step_4_body',  'The Largest Value Leak panel quantifies the gap in dollars, projects the recovery if you close it, and routes you to the Portfolio Analysis tool to model the fix \u2014 then back to the chain to validate.');
  // Dynamic leak step copy — `{stage}` and `{pct}` are interpolated
  // with the live steepest-drop stage at render time.
  const leakTitleTpl = useMessaging('tour.leak_title', 'This quarter, value is leaking at {stage}');
  const leakBodyTpl  = useMessaging('tour.leak_body',  '{stage} fell the most quarter-over-quarter ({pct}). It\u2019s the single highest-leverage place to intervene right now \u2014 fix it here and the gains compound downstream.');

  // Live value leak (shared detector). When present, we splice a
  // concrete "your value is leaking at <stage>" step that spotlights
  // the actual worst ChainNode, right after the generic leak step.
  const [leak, refreshLeak] = useChainLeak();

  const steps = useMemo(() => {
    const fill = (tpl, stageLabel, pct) =>
      String(tpl).replace(/\{stage\}/g, stageLabel).replace(/\{pct\}/g, pct);
    const list = [
      { targetTestid: 'learning-impact-chain-band',           title: s1Title, body: s1Body },
      { targetTestid: 'missing-middle-bracket',                title: s2Title, body: s2Body },
      { targetTestid: 'chain-arrow-performance-to-outcomes',   title: s3Title, body: s3Body },
    ];
    if (leak?.stage) {
      const stageLabel = leak.stage.label;
      const pct = `${leak.deltaPct}%`;
      list.push({
        targetTestid: leak.stage.nodeTestid,
        title: fill(leakTitleTpl, stageLabel, pct),
        body: fill(leakBodyTpl, stageLabel, pct),
      });
    }
    list.push({ targetTestid: 'home-weak-link-eyebrow', title: s4Title, body: s4Body });
    return list;
  }, [s1Title, s1Body, s2Title, s2Body, s3Title, s3Body, s4Title, s4Body, leakTitleTpl, leakBodyTpl, leak]);

  const total = steps.length;
  const current = steps[stepIdx];
  const [tipPos, setTipPos] = useState(null);

  // Apply / clear the spotlight ring + position the tooltip card
  // relative to the highlighted target.
  useEffect(() => {
    if (!open || !current) return undefined;
    ensureSpotlightCss();

    // Clear the prior target.
    if (prevTargetRef.current) {
      prevTargetRef.current.removeAttribute('data-tour-active');
      prevTargetRef.current = null;
    }

    const el = document.querySelector(`[data-testid="${current.targetTestid}"]`);
    if (!el) {
      setTipPos(null);
      return undefined;
    }

    // Scroll into view BEFORE measuring so the rect we compute is the
    // post-scroll one (otherwise the tooltip ends up off-screen).
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.setAttribute('data-tour-active', 'true');
    prevTargetRef.current = el;

    const measure = () => {
      const r = el.getBoundingClientRect();
      setTipPos({
        top: Math.min(window.innerHeight - 280, Math.max(20, r.bottom + 16)),
        left: Math.min(window.innerWidth - 420, Math.max(20, r.left)),
      });
    };
    // Two ticks — once immediately, once after scroll completes.
    measure();
    const t = setTimeout(measure, 350);

    return () => {
      clearTimeout(t);
      el.removeAttribute('data-tour-active');
    };
  }, [open, stepIdx, current]);

  // ESC closes; arrow keys navigate.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') setOpen(false);
      if (e.key === 'ArrowRight') setStepIdx((i) => Math.min(total - 1, i + 1));
      if (e.key === 'ArrowLeft')  setStepIdx((i) => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, total]);

  const start = useCallback(() => {
    setStepIdx(0);
    setOpen(true);
  }, []);

  const finish = useCallback(() => {
    setOpen(false);
    if (prevTargetRef.current) {
      prevTargetRef.current.removeAttribute('data-tour-active');
      prevTargetRef.current = null;
    }
  }, []);

  // Build a sharable URL with `?tour=auto` so the recipient lands
  // straight into the auto-running tour. Optionally pin the org so
  // super-admins can pre-scope the demo (e.g. for a specific
  // prospect's data context).
  const buildShareUrl = useCallback(() => {
    if (typeof window === 'undefined') return '';
    const base = `${window.location.origin}${window.location.pathname}`;
    const params = new URLSearchParams();
    params.set('tour', 'auto');
    if (viewAsOrg?.id) params.set(SHARABLE_ORG_PARAM, viewAsOrg.id);
    return `${base}?${params.toString()}`;
  }, [viewAsOrg]);

  // One-click demo prep: (1) freshly seed the "Missing Middle" story
  // for the previewed/current org so the chain visibly leaks at
  // Behavior with Adoption as the binding constraint, then (2) copy a
  // `?tour=auto` deep-link so the recipient lands straight into the
  // self-running 60-second tour over real, story-consistent data.
  const copyShareLink = useCallback(async () => {
    const url = buildShareUrl();
    if (!url) return;
    setSeeding(true);
    let seeded = false;
    try {
      const qs = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
      await api.post(`/admin/data-health/seed-missing-middle-story${qs}`);
      seeded = true;
      // Re-pull the leak so the readiness chip flips to "live" inline.
      await refreshLeak();
    } catch {
      // Seeding is best-effort — if it fails (e.g. org already has rich
      // data), we still hand over a working tour link rather than block.
    }
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      toast.success(
        seeded
          ? 'Demo seeded + link copied — recipient lands in the auto-tour'
          : 'Demo link copied — paste anywhere to share',
      );
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Older browsers / iframes that block clipboard
      toast.error('Could not access clipboard — copy manually: ' + url);
    } finally {
      setSeeding(false);
    }
  }, [buildShareUrl, viewAsOrg, refreshLeak]);

  // Inline "fix readiness" — when the demo isn't showing a leak yet,
  // the readiness chip itself becomes a one-click seeder so a rep can
  // make the story live without touching the Copy button.
  const seedAndRefresh = useCallback(async () => {
    setSeeding(true);
    try {
      const qs = viewAsOrg?.id ? `?organization_id=${viewAsOrg.id}` : '';
      await api.post(`/admin/data-health/seed-missing-middle-story${qs}`);
      const next = await refreshLeak();
      toast.success(
        next?.stage
          ? `Demo ready — ${next.stage.label} ${next.deltaPct}% is live`
          : 'Demo seeded — refreshing…',
      );
    } catch {
      toast.error('Could not seed the demo story — try the Copy button or reseed from Data Management.');
    } finally {
      setSeeding(false);
    }
  }, [viewAsOrg, refreshLeak]);

  // Auto-launch via `?tour=auto` deep-link. Lets sales send a single
  // sharable URL that boots a logged-in user straight into the tour.
  // We strip the param after consuming it so a manual refresh doesn't
  // re-trigger it endlessly.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('tour') !== 'auto') return;
    // Small delay so the chain band + leak panel have rendered.
    const t = setTimeout(() => {
      setStepIdx(0);
      setOpen(true);
      params.delete('tour');
      const next = params.toString();
      const url = `${window.location.pathname}${next ? `?${next}` : ''}${window.location.hash || ''}`;
      window.history.replaceState({}, '', url);
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      {/* Floating launcher — bottom-right of the home page.
          Super-admins also see a "Copy demo link" affordance for
          easy sharing of the `?tour=auto` deep-link with prospects. */}
      {/* Floating launcher — shown only when demo tools are enabled
          (Profile settings or the Home header toggle). The tour overlay
          and `?tour=auto` auto-launch below always stay available. */}
      {demoTools && !open && (
        <div className="fixed bottom-6 right-6 z-40 flex items-center gap-2" data-testid="missing-middle-tour-launcher-group">
          {isSuperAdmin && (
            <>
              {/* Demo-readiness preflight chip — reads the same live leak
                  detector the chain/tour use, so a rep can confirm the
                  seeded story is actually rendering (green) before they
                  copy + share a link. When NOT ready, the chip itself is
                  a one-click seeder that makes the story live inline. */}
              {leak?.stage ? (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-full font-semibold text-[11px] shadow-lg ring-1 bg-emerald-50 text-emerald-700 ring-emerald-200"
                  data-testid="missing-middle-demo-readiness"
                  data-ready="true"
                  title="Demo data is live — the chain is leaking, safe to share"
                >
                  <Check className="h-3.5 w-3.5 shrink-0" />
                  {leak.stage.label} {leak.deltaPct}% live
                </span>
              ) : (
                <button
                  type="button"
                  onClick={seedAndRefresh}
                  disabled={seeding}
                  className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-full font-semibold text-[11px] shadow-lg ring-1 bg-amber-50 text-amber-700 ring-amber-200 hover:bg-amber-100 hover:scale-[1.03] transition-all disabled:opacity-60 disabled:cursor-wait disabled:hover:scale-100"
                  data-testid="missing-middle-demo-readiness"
                  data-ready="false"
                  aria-label="Demo not ready — click to seed the story and make the leak live"
                  title="No leak detected — click to seed the Missing-Middle story and make it live"
                >
                  {seeding ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                      Making demo live…
                    </>
                  ) : (
                    <>
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
                      Demo not ready — fix it
                    </>
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={copyShareLink}
                disabled={seeding}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-full bg-white text-slate-700 font-semibold text-xs shadow-xl shadow-slate-900/10 ring-1 ring-slate-200 hover:bg-slate-50 hover:scale-[1.03] transition-all disabled:opacity-60 disabled:cursor-wait disabled:hover:scale-100"
                data-testid="missing-middle-tour-copy-link"
                aria-label="Seed the demo story and copy a sharable link with the tour auto-launching"
                title="Seeds the Missing-Middle story, then copies a link — recipient lands straight into the tour"
              >
                {seeding ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Seeding demo…
                  </>
                ) : linkCopied ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                    Copied
                  </>
                ) : (
                  <>
                    <Link2 className="h-3.5 w-3.5" />
                    Copy demo link
                  </>
                )}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={start}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-500 text-white font-semibold text-sm shadow-2xl shadow-blue-900/40 ring-1 ring-cyan-300/30 hover:scale-[1.03] transition-transform"
            data-testid="missing-middle-tour-launcher"
            aria-label="Launch the 60-second product tour"
          >
            <Sparkles className="h-4 w-4" />
            Take the 60-sec Tour
          </button>
        </div>
      )}

      {/* Tour overlay — backdrop + tooltip card. */}
      {open && (
        <>
          {/* Backdrop. Click to dismiss. */}
          <div
            className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-[1px] transition-opacity"
            onClick={finish}
            data-testid="missing-middle-tour-backdrop"
          />
          {/* Tooltip card. */}
          <div
            className="fixed z-[70] w-[400px] max-w-[calc(100vw-40px)] rounded-xl bg-gradient-to-br from-indigo-950 via-blue-900 to-cyan-900 text-blue-50 ring-1 ring-cyan-400/30 shadow-2xl p-5"
            style={tipPos
              ? { top: `${tipPos.top}px`, left: `${tipPos.left}px` }
              : { bottom: '24px', left: '50%', transform: 'translateX(-50%)' }
            }
            data-testid="missing-middle-tour-card"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-cyan-300 font-semibold">
                Mission Control · Tour · {stepIdx + 1} of {total}
              </div>
              <button
                type="button"
                onClick={finish}
                className="text-blue-100/60 hover:text-white transition-colors"
                aria-label="Close tour"
                data-testid="missing-middle-tour-close"
              >
                <XIcon className="h-4 w-4" />
              </button>
            </div>

            <h3 className="text-lg font-bold text-white mt-1.5" data-testid="missing-middle-tour-title">
              {current.title}
            </h3>
            <p className="text-sm text-blue-100/85 mt-2 leading-relaxed" data-testid="missing-middle-tour-body">
              {current.body}
            </p>

            {/* Progress dots */}
            <div className="flex items-center gap-1.5 mt-4">
              {steps.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === stepIdx
                      ? 'w-6 bg-cyan-300'
                      : i < stepIdx
                        ? 'w-1.5 bg-cyan-500/70'
                        : 'w-1.5 bg-blue-100/30'
                  }`}
                />
              ))}
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between mt-4 gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
                disabled={stepIdx === 0}
                className="text-blue-100 hover:bg-white/5 hover:text-white disabled:opacity-40"
                data-testid="missing-middle-tour-prev"
              >
                <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                Back
              </Button>
              {stepIdx < total - 1 ? (
                <Button
                  size="sm"
                  onClick={() => setStepIdx((i) => Math.min(total - 1, i + 1))}
                  className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold"
                  data-testid="missing-middle-tour-next"
                >
                  Next
                  <ArrowRightIcon className="h-3.5 w-3.5 ml-1" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={finish}
                  className="bg-emerald-400 hover:bg-emerald-300 text-slate-900 font-semibold"
                  data-testid="missing-middle-tour-finish"
                >
                  <PlayCircle className="h-3.5 w-3.5 mr-1" />
                  Done
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
